import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack
} from '@mui/material';
import { 
  getCollectionMetadata, 
  getDefaultDateRange, 
  validateDateRange,
  type GEEMetadata,
  type DateRange 
} from '../services/geeService';

interface GEEDateRangeSelectorProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (dateRange: DateRange) => void;
  collectionId: string;
  title?: string;
}

const GEEDateRangeSelector: React.FC<GEEDateRangeSelectorProps> = ({
  open,
  onClose,
  onConfirm,
  collectionId,
  title = 'Select Date Range'
}) => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [metadata, setMetadata] = useState<GEEMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  // Load collection metadata and set default dates
  useEffect(() => {
    if (open && collectionId) {
      setLoading(true);
      setError(null);
      
      getCollectionMetadata(collectionId)
        .then((meta) => {
          setMetadata(meta);
          const defaultRange = getDefaultDateRange(collectionId);
          setStartDate(defaultRange.startDate);
          setEndDate(defaultRange.endDate);
        })
        .catch((err) => {
          setError(`Failed to load collection metadata: ${err.message}`);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, collectionId]);

  const handleConfirm = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const dateRange: DateRange = {
        startDate: startDate,
        endDate: endDate
      };

      // Validate date range
      validateDateRange(dateRange.startDate, dateRange.endDate);
      
      onConfirm(dateRange);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid date range');
    } finally {
      setIsValidating(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const getQuickDateRanges = () => {
    const now = new Date();
    const ranges = [
      { label: 'Last 7 days', days: 7 },
      { label: 'Last 30 days', days: 30 },
      { label: 'Last 3 months', days: 90 },
      { label: 'Last 6 months', days: 180 },
      { label: 'Last year', days: 365 }
    ];

    return ranges.map(range => {
      const start = new Date(now.getTime() - (range.days * 24 * 60 * 60 * 1000));
      return {
        ...range,
        startDate: start.toISOString().split('T')[0],
        endDate: now.toISOString().split('T')[0]
      };
    });
  };

  const handleQuickRange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    setError(null);
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        {metadata && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {metadata.name} - {metadata.description}
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {metadata && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Collection Information:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip 
                label={`Available from: ${new Date(metadata.startDate).toLocaleDateString()}`} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                label={`Cloud filter: ${metadata.cloudFilter}%`} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                label={`Bands: ${metadata.bands.join(', ')}`} 
                size="small" 
                variant="outlined" 
              />
            </Stack>
          </Box>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Quick Date Ranges:
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {getQuickDateRanges().map((range, index) => (
              <Chip
                key={index}
                label={range.label}
                size="small"
                clickable
                onClick={() => handleQuickRange(range.startDate, range.endDate)}
                variant={startDate === range.startDate && endDate === range.endDate ? "filled" : "outlined"}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setError(null);
            }}
            inputProps={{
              max: endDate || new Date().toISOString().split('T')[0]
            }}
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setError(null);
            }}
            inputProps={{
              min: startDate,
              max: new Date().toISOString().split('T')[0]
            }}
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>

        {startDate && endDate && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Selected range: {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
              <br />
              Duration: {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))} days
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isValidating}>
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          disabled={!startDate || !endDate || isValidating}
          startIcon={isValidating ? <CircularProgress size={16} /> : null}
        >
          {isValidating ? 'Validating...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GEEDateRangeSelector; 